import {createAsyncThunk} from '@reduxjs/toolkit';
import {isEqual} from 'lodash-es';

import {ILookupEntry} from 'shared/types/lookups';
import {
    IFetchDataByIdArgsNumberType,
    TRequestTableResponseWithPagination,
} from 'shared/types/requests';
import {TDescriptiveFlexFieldData} from 'store/slices/descriptive-flexfields-slice/descriptive-flexfields-slice-types';

import {
    TDeleteAisDocumentsInRegisterArgs,
    TDeletePUDDocumentsArgs,
    TFetchAisDocumentsRegisterParams,
    TFetchAisIncludedDocumentsByTaxArgs,
    fetchAisRequirements,
    TFetchAisRequirementsParams,
    TPostAisDocumentInRegisterArgs,
    IPostPUDFileToDocumentArgs,
    TPostUpdateAisDocumentsActiveFlag,
    TPutAisDocumentInRegisterArgs,
    deleteAisDocumentsByTax,
    deleteAisDocumentsInRegister,
    deletePUDDocuments,
    fetchAisDocumentsByTaxRegister,
    fetchAisDocumentsRegister,
    fetchAisIncludedDocumentsByTax,
    postAisDocumentByTax,
    postAisDocumentInRegister,
    postPUDFileToDocument,
    postUpdateAisDocumentsActiveFlag,
    putAisDocumentByTax,
    putAisDocumentInRegister,
    fetchAisDocumentsRegisterFields,
    TAisDocumentsRegisterSettings,
    fetchAisDocumentsRegisterSettings,
    TAisDocRegisterFieldsRaw,
    fetchAisDocAdditionalFieldsByDocId,
    postUpdateAisDocumentsAdditionalFields,
    TPostUpdateAisDocumentsAdditionalFields,
    TFetchAisRequestsToPublishParams,
    fetchAisRequestsToPublish,
    fetchAisRequests,
    TFormTransportContainerProtocolRequestArgs,
    formTransportContainerProtocolRequest,
    fetchAisDocumentsInterfaceFields,
    fetchAisDocumentsInterfaceData,
    TFetchAisDocumentsInterfaceDataParams,
    deleteAisDocumentsInInterface,
    TDeleteAisDocumentsInInterfaceArgs,
    TAisDocInterfaceFieldsRaw,
    updateAisDocumentInterfaceCard,
    TUpdateAisDocumentInterfaceCardArgs,
    TPublishAisInterfaceDocumentsArgs,
    publishAisInterfaceDocuments,
    deleteAisDocumentsInInterfaceByFilter,
    TDeleteAisDocumentsInInterfaceByFilterArgs,
    updateStatusAisDocumentsInInterface,
    TUpdateStatusAisDocumentsInInterfaceArgs,
    updateStatusAisDocumentsInInterfaceByFilter,
    TUpdateStatusAisDocumentsInInterfaceByFilterArgs,
    fetchAisDocumentsRegisterFilterConditions,
    TFetchAisDocumentsByTaxRegisterParams,
} from './ais-slice-api';
import {AIS3_SLICE} from './ais-slice-constants';
import {
    IAisInterfaceDocumentDto,
    TAisRegisterDocumentByTaxDto,
    IAisRegisterDocumentDto,
    IAisRegisterTax,
    IAisRequestDto,
    IAisRequestToPublishDto,
    IAisRequirementDto,
} from './ais-slice-types';

// ===== Documents By Tax =====

export const loadAisRequests = createAsyncThunk<IAisRequestDto[], TFetchAisRequirementsParams>(
    `${AIS3_SLICE}/loadAisRequests`,
    async (arg, {rejectWithValue}) => {
        try {
            const response = await fetchAisRequests(arg ?? undefined);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const loadAisRequirements = createAsyncThunk<IAisRequirementDto[], TFetchAisRequirementsParams>(
    `${AIS3_SLICE}/loadAisRequirements`,
    async (arg, {rejectWithValue}) => {
        try {
            const response = await fetchAisRequirements(arg ?? undefined);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const loadAisDocumentsByTaxRegister = createAsyncThunk<
    TRequestTableResponseWithPagination<TAisRegisterDocumentByTaxDto>, TFetchAisDocumentsByTaxRegisterParams | void
>(
    `${AIS3_SLICE}/getAisDocumentsByTaxRegister`,
    async (arg, {rejectWithValue}) => {
        try {
            const response = await fetchAisDocumentsByTaxRegister(arg ?? undefined);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

// ===== Documents =====

export const loadAisRequestsToPublish = createAsyncThunk<IAisRequestToPublishDto[], TFetchAisRequestsToPublishParams>(
    `${AIS3_SLICE}/loadAisRequestsToPublish`,
    async (arg, {rejectWithValue}) => {
        try {
            const response = await fetchAisRequestsToPublish(arg ?? undefined);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const removeSinglePUDDocument = createAsyncThunk<
    string, {
        aisDocId: string;
        id: number;
    }
>(
    `${AIS3_SLICE}/removeSinglePUDDocument`, async (arg, {rejectWithValue}) => {
        try {
            const {aisDocId} = arg;
            const response = await deletePUDDocuments({ids: [aisDocId]});
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const removePUDDocuments = createAsyncThunk<
    string, TDeletePUDDocumentsArgs
>(
    `${AIS3_SLICE}/removePUDDocuments`, async (arg, {rejectWithValue}) => {
        try {
            const response = await deletePUDDocuments(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const uploadPUDFileToDocument = createAsyncThunk<string, {
    arg: IPostPUDFileToDocumentArgs;
    id: number;
}>(
    `${AIS3_SLICE}/uploadPUDFileToDocument`,
    async (args, {rejectWithValue}) => {
        try {
            const {arg} = args;
            const response = await postPUDFileToDocument(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const loadAisDocumentsRegister = createAsyncThunk<
    TRequestTableResponseWithPagination<IAisRegisterDocumentDto>, TFetchAisDocumentsRegisterParams | void
>(
    `${AIS3_SLICE}/getAisDocumentsRegister`,
    async (arg, {rejectWithValue}) => {
        try {
            const response = await fetchAisDocumentsRegister(arg ?? undefined);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const loadAisDocumentsRegisterFilterConditionsEntries = createAsyncThunk<
    ILookupEntry[], void
>(
    `${AIS3_SLICE}/loadAisDocumentsRegisterFilterConditionsEntries`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchAisDocumentsRegisterFilterConditions();
            return response.data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateAisDocumentInRegister = createAsyncThunk<
    IAisRegisterDocumentDto, TPutAisDocumentInRegisterArgs
>(
    `${AIS3_SLICE}/updateAisDocumentInRegister`, async (arg, {rejectWithValue}) => {
        try {
            const response = await putAisDocumentInRegister(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const createAisDocumentInRegister = createAsyncThunk<
    IAisRegisterDocumentDto, TPostAisDocumentInRegisterArgs
>(
    `${AIS3_SLICE}/createAisDocumentInRegister`, async (arg, {rejectWithValue}) => {
        try {
            const response = await postAisDocumentInRegister(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const removeAisDocumentsInRegister = createAsyncThunk<
    string, TDeleteAisDocumentsInRegisterArgs
>(
    `${AIS3_SLICE}/deleteAisDocumentsInRegister`, async (arg, {rejectWithValue}) => {
        try {
            const response = await deleteAisDocumentsInRegister(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateAisDocumentsActiveFlagInRegister = createAsyncThunk<
    string, TPostUpdateAisDocumentsActiveFlag
>(
    `${AIS3_SLICE}/updateAisDocumentsActiveFlagInRegister`, async (arg, {rejectWithValue}) => {
        try {
            const response = await postUpdateAisDocumentsActiveFlag(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const loadAisDocumentsRegisterSettings = createAsyncThunk<
    TAisDocumentsRegisterSettings, void
    >(
        `${AIS3_SLICE}/loadAisDocumentsRegisterSettings`,
        async (arg, {rejectWithValue}) => {
            try {
                const response = await fetchAisDocumentsRegisterSettings();
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const loadAisDocumentsRegisterFields = createAsyncThunk<
    TAisDocRegisterFieldsRaw, void
    >(
        `${AIS3_SLICE}/loadAisDocumentsRegisterFields`,
        async (arg, {rejectWithValue}) => {
            try {
                const response = await fetchAisDocumentsRegisterFields();
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

// ===== Included Documents By Tax =====

export const loadAisIncludedDocumentsByTax = createAsyncThunk<
    TAisRegisterDocumentByTaxDto[], TFetchAisIncludedDocumentsByTaxArgs
>(
    `${AIS3_SLICE}/loadIncludedAisDocumentsByTax`,
    async (arg, {rejectWithValue}) => {
        try {
            const response = await fetchAisIncludedDocumentsByTax(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

interface IUpdateAisIncludedDocumentsByTaxArgs {
    baseDocumentId?: number;
    currentDocumentsByTax: TAisRegisterDocumentByTaxDto[];
    newDocumentsByTax: (Partial<TAisRegisterDocumentByTaxDto> | undefined)[];
}
export const updateAisIncludedDocumentsByTaxList = createAsyncThunk<
    TAisRegisterDocumentByTaxDto[] | undefined, IUpdateAisIncludedDocumentsByTaxArgs
>(
    `${AIS3_SLICE}/updateIncludedAisDocumentsByTax`,
    async (arg, {rejectWithValue, dispatch}) => {
        try {
            const {currentDocumentsByTax, newDocumentsByTax, baseDocumentId} = arg;

            const currentIds: number[] = currentDocumentsByTax
                .filter(d => d?.mappingId || d?.mappingId === 0).map(d => d.mappingId);
            const nextIds: number[] = newDocumentsByTax
                .filter(d => d?.mappingId || d?.mappingId === 0).map(d => d!.mappingId as number);

            const docsByTaxIdsToDelete = currentIds.filter(id => !nextIds.includes(id));
            const docsByTaxToCreate = newDocumentsByTax.filter(docByTax => docByTax && !docByTax?.mappingId) as Partial<
                TAisRegisterDocumentByTaxDto>[];
            const docsByTaxToEdit = (() => {
                const nextTaxesPreserved = newDocumentsByTax.filter(docByTax => docByTax?.mappingId
                    && currentIds.includes(docByTax.mappingId));
                const nextTaxesModified = nextTaxesPreserved.filter(docByTax => {
                    const currentDocumentByTax = currentDocumentsByTax.find(dbt => dbt.mappingId
                        === docByTax?.mappingId);
                    if (currentDocumentByTax && !isEqual(currentDocumentByTax, docByTax)) return true;
                    return false;
                });
                return nextTaxesModified as Partial<TAisRegisterDocumentByTaxDto>[];
            })();

            const CRUDPromises = (() => {
                const promises: Promise<any>[] = [];
                if (docsByTaxToEdit.length) {
                    docsByTaxToEdit.forEach(doc => {
                        if (baseDocumentId) {
                            promises.push(putAisDocumentByTax({
                                ...doc as IAisRegisterTax,
                                id: baseDocumentId,
                            }));
                        }
                    });
                }
                if (docsByTaxIdsToDelete.length) {
                    promises.push(deleteAisDocumentsByTax({
                        ids: docsByTaxIdsToDelete,
                    }));
                }
                if (docsByTaxToCreate.length) {
                    docsByTaxToCreate.forEach(doc => {
                        if (baseDocumentId) {
                            promises.push(postAisDocumentByTax({
                                ...doc as IAisRegisterTax,
                                id: baseDocumentId,
                            }));
                        }
                    });
                }
                return promises;
            })();

            await Promise.all(CRUDPromises);

            if (!baseDocumentId) return undefined;

            const loadedDocumentsByTax = await dispatch(loadAisIncludedDocumentsByTax({id: baseDocumentId})).unwrap();

            return loadedDocumentsByTax;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

// ===== Documents Register AdditionalFields =====

export const loadAisDocumentAdditionalFieldsByDocId = createAsyncThunk<
    TDescriptiveFlexFieldData, IFetchDataByIdArgsNumberType
    >(
        `${AIS3_SLICE}/loadAisDocumentAdditionalFieldsByDocId`,
        async (arg, {rejectWithValue}) => {
            try {
                const response = await fetchAisDocAdditionalFieldsByDocId(arg);
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const updateAisDocumentsAdditionalFields = createAsyncThunk<
    string, TPostUpdateAisDocumentsAdditionalFields
    >(
        `${AIS3_SLICE}/updateAisDocumentsAdditionalFields`,
        async (arg, {rejectWithValue}) => {
            try {
                try {
                    const response = await postUpdateAisDocumentsAdditionalFields(arg);
                    const {data} = response;
                    return data;
                } catch (e) {
                    return rejectWithValue(e);
                }
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const formTransportContainerProtocol = createAsyncThunk<
    string, TFormTransportContainerProtocolRequestArgs
    >(
        `${AIS3_SLICE}/formTransportContainerProtocol`,
        async (arg, {rejectWithValue}) => {
            try {
                const response = await formTransportContainerProtocolRequest(arg);
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

// ===== Documents Interface =====

export const loadAisDocumentsInterfaceFields = createAsyncThunk<
    TAisDocInterfaceFieldsRaw, void
    >(
        `${AIS3_SLICE}/loadAisDocumentsInterfaceFields`,
        async (arg, {rejectWithValue}) => {
            try {
                const response = await fetchAisDocumentsInterfaceFields();
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const loadAisDocumentsInterfaceData = createAsyncThunk<
    TRequestTableResponseWithPagination<IAisInterfaceDocumentDto>, TFetchAisDocumentsInterfaceDataParams | void
>(
    `${AIS3_SLICE}/getAisDocumentsInterfaceData`,
    async (arg, {rejectWithValue}) => {
        try {
            const response = await fetchAisDocumentsInterfaceData(arg ?? undefined);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const removeAisDocumentsInInterface = createAsyncThunk<
    string, TDeleteAisDocumentsInInterfaceArgs
>(
    `${AIS3_SLICE}/removeAisDocumentsInInterface`, async (arg, {rejectWithValue}) => {
        try {
            const response = await deleteAisDocumentsInInterface(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const removeAisDocumentsInInterfaceByFilter = createAsyncThunk<
    string, TDeleteAisDocumentsInInterfaceByFilterArgs
>(
    `${AIS3_SLICE}/removeAisDocumentsInInterfaceByFilter`, async (arg, {rejectWithValue}) => {
        try {
            const response = await deleteAisDocumentsInInterfaceByFilter(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const changeStatusAisDocumentsInInterface = createAsyncThunk<
    string, TUpdateStatusAisDocumentsInInterfaceArgs
>(
    `${AIS3_SLICE}/changeStatusAisDocumentsInInterface`, async (arg, {rejectWithValue}) => {
        try {
            const response = await updateStatusAisDocumentsInInterface(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const changeStatusAisDocumentsInInterfaceByFilter = createAsyncThunk<
    string, TUpdateStatusAisDocumentsInInterfaceByFilterArgs
>(
    `${AIS3_SLICE}/changeStatusAisDocumentsInInterfaceByFilter`, async (arg, {rejectWithValue}) => {
        try {
            const response = await updateStatusAisDocumentsInInterfaceByFilter(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const publishAisDocumentsInInterface = createAsyncThunk<
    string, TPublishAisInterfaceDocumentsArgs
>(
    `${AIS3_SLICE}/publishAisDocumentsInInterface`, async (arg, {rejectWithValue}) => {
        try {
            const response = await publishAisInterfaceDocuments(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateAisDocumentInInterfaceCard = createAsyncThunk<
    IAisInterfaceDocumentDto, TUpdateAisDocumentInterfaceCardArgs
>(
    `${AIS3_SLICE}/updateAisDocumentInInterfaceCard`, async (arg, {rejectWithValue}) => {
        try {
            const response = await updateAisDocumentInterfaceCard(arg);
            const {data} = response;
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);
